import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router";

import { HStack, VStack } from "../../../components/utils";
import {
  GrantedBy,
  useGrantDetailsStore,
} from "../../../store/useGrantDetailsStore";
import {
  AllocationMode,
  CostOrGain,
  NoMatchBehaviour,
  RuleScope,
} from "../../../store/useAllocationMatrixStore";
import {
  AllocationEvaluation,
  useEmployeeGrants,
  useEvaluateMatrixQuery,
} from "../../../queries/allocationMatrix";
import {
  formatCurrency,
  getCurrencyType,
} from "../../../utils/currencyFormatter";
import { useValuationDetails } from "../../../queries/Valuation";
import { ValuationSource } from "../../../types/ValuationType";
import { useCompanyStore } from "../../../store";
import { calculateAllocationValue } from "../../../utils/allocationMatrixValue";

function FormulaTooltip({ costOrGain }: { costOrGain: CostOrGain }) {
  const isGain = costOrGain === CostOrGain.GAIN;
  return (
    <div className="relative group inline-flex items-center">
      <Icon
        icon="ri:information-line"
        height={14}
        className="text-gray-400 cursor-pointer hover:text-gray-600"
      />
      <div className="absolute top-full left-0 mt-2 hidden group-hover:block z-50 pointer-events-none">
        <div className="w-2.5 h-2.5 bg-white border-t border-l border-gray-200 rotate-45 ml-2 -mb-[5px]" />
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 whitespace-nowrap">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            {isGain ? "Gain Formula" : "Cost Formula"}
          </p>
          <div className="flex items-baseline gap-1 text-sm font-medium text-gray-800">
            <span className="italic">V</span>
            <span className="text-gray-400 mx-0.5">=</span>
            <span className="italic">n</span>
            <span className="text-gray-400 mx-0.5">×</span>
            {isGain ? (
              <>
                <span className="text-gray-500">(</span>
                <span className="italic text-blue-600">FMV</span>
                <span className="text-gray-400 mx-0.5">−</span>
                <span className="italic text-orange-501">P</span>
                <sub className="text-orange-501 text-xs -ml-0.5">strike</sub>
                <span className="text-gray-500">)</span>
              </>
            ) : (
              <span className="italic text-blue-600">FMV</span>
            )}
          </div>
          <div className="mt-2.5 pt-2 border-t border-gray-100 flex flex-col gap-0.5 text-xs text-gray-500">
            <span>
              <span className="italic font-semibold text-gray-700">V</span> —
              monetary value
            </span>
            <span>
              <span className="italic font-semibold text-gray-700">n</span> —
              number of options
            </span>
            <span>
              <span className="italic font-semibold text-blue-600">FMV</span> —
              fair market value (share price)
            </span>
            {isGain && (
              <span>
                <span className="italic font-semibold text-orange-501">P</span>
                <sub className="text-xs text-orange-501">strike</sub> — grant /
                strike price
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AllocatedTooltip({
  companyType,
  currentSharePrice,
}: {
  companyType: string;
  currentSharePrice: number;
}) {
  const isPublic = companyType === "PUBLIC";
  return (
    <div className="relative group inline-flex items-center">
      <Icon
        icon="ri:information-line"
        height={14}
        className="text-gray-400 cursor-pointer hover:text-gray-600"
      />
      <div className="absolute top-full left-0 mt-2 hidden group-hover:block z-50 pointer-events-none">
        <div className="w-2.5 h-2.5 bg-white border-t border-l border-gray-200 rotate-45 ml-2 -mb-[5px]" />
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 whitespace-nowrap">
          <p className="text-xs text-gray-600">
            Calculated using the{" "}
            {isPublic ? "latest share price" : "latest valuation"} as of today (
            {formatCurrency(currentSharePrice, getCurrencyType())}).
          </p>
        </div>
      </div>
    </div>
  );
}

type TabId = "eligibility" | "rules" | "utilization";

const TABS: { id: TabId; label: string }[] = [
  { id: "eligibility", label: "Eligibility" },
  { id: "rules", label: "Allocation Rules" },
  { id: "utilization", label: "Utilization History" },
];

const NO_MATCH_MESSAGE: Record<NoMatchBehaviour, string> = {
  [NoMatchBehaviour.BLOCK]:
    "No matching rule — grants are blocked for this recipient.",
  [NoMatchBehaviour.FALLBACK_RULE]:
    "No specific rule matched — the fallback rule applies.",
  [NoMatchBehaviour.MANUAL_OVERRIDE]:
    "No matching rule — a manual override is required.",
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function formatFactorLabel(field: string) {
  return field.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
}

export type AllocationEnvelopeOverrides = {
  allocationMatrixId?: string;
  employeeId?: string;
  employeeName?: string;
  grantId?: string;
  sharePrice?: number;
  grantPrice?: number;
  optionsGranted?: number;
  amount?: number;
  grantedBy?: GrantedBy;
  currentSharePrice?: number;
  employeeGrants?: any[];
  embedded?: boolean;
  evaluation?: AllocationEvaluation | null;
  onViewGrantHistory?: () => void;
  managerScoped?: boolean;
};

const AllocationEnvelopePanel = ({
  overrides,
}: {
  overrides?: AllocationEnvelopeOverrides;
}) => {
  const currencyType = getCurrencyType();
  const data = useGrantDetailsStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>("eligibility");

  const matrixId = overrides?.allocationMatrixId ?? data.allocationMatrixId;
  const employeeId = overrides?.employeeId ?? data.employee?.id;
  const sharePrice = overrides?.sharePrice ?? data.sharePrice;
  const grantPrice = overrides?.grantPrice ?? data.grantPrice;
  const optionsGranted = overrides?.optionsGranted ?? data.optionsGranted;
  const amount = overrides?.amount ?? data.amount;
  const grantedByVal = overrides?.grantedBy ?? data.grantedBy;
  const editingGrantId = overrides?.grantId ?? data.grantId;
  const initializedTabForRef = useRef<string | null>(null);
  const initialTabKey = `${matrixId ?? ""}:${employeeId ?? ""}:${
    editingGrantId ?? ""
  }`;
  const evaluationOverride = overrides?.evaluation;
  const { data: evaluatedMatrix, isFetching } = useEvaluateMatrixQuery(
    matrixId,
    employeeId,
    editingGrantId,
    !evaluationOverride
  );
  const evaluation = evaluationOverride ?? evaluatedMatrix;
  const employeeGrantsOverride = overrides?.employeeGrants;
  const { data: employeeGrantsQueryData, isFetching: grantsFetching } =
    useEmployeeGrants(employeeId, !employeeGrantsOverride);
  const employeeGrantsData = employeeGrantsOverride ?? employeeGrantsQueryData;

  const overrideSharePrice = overrides?.currentSharePrice;
  const { data: _valuationData, isPlaceholderData } = useValuationDetails(
    overrides?.managerScoped
  );
  const { companyData } = useCompanyStore();

  // Determine current share price for utilization calculation
  // Priority: caller-supplied value (e.g. manager portal) → company PPS (public)
  // → latest valuation PPS (private) → form input
  const currentSharePrice = useMemo(() => {
    if (overrideSharePrice !== undefined) return overrideSharePrice;

    const isPublic = companyData?.companyMetaData.typeOfTheCompany === "PUBLIC";

    if (isPublic) {
      // For public companies, use stored PPS
      const publicPps = companyData?.companyMetaData?.pps;
      if (publicPps) return publicPps;
    } else if (
      _valuationData &&
      !isPlaceholderData &&
      _valuationData.length > 0
    ) {
      // For private companies, use latest valuation's PPS
      const sortedValuations = [..._valuationData].sort(
        (a: any, b: any) =>
          new Date(b.dateOfValuation).getTime() -
          new Date(a.dateOfValuation).getTime()
      );
      let valuation = sortedValuations.find(
        (v: any) => v.valuationSource === ValuationSource.COMPANY
      );
      if (!valuation) {
        // Fallback to latest valuation regardless of source
        valuation = sortedValuations[0];
      }
      if (valuation?.pps) return valuation.pps;
    }
    // Fallback to form input if no company/valuation data available
    return sharePrice || 0;
  }, [
    overrideSharePrice,
    _valuationData,
    isPlaceholderData,
    companyData,
    sharePrice,
  ]);

  const evalData: AllocationEvaluation = evaluation ?? {};
  // `rule` contains creation-time headroom adjusted by the backend. Use the
  // original matched rule for displaying the envelope, then subtract the
  // authoritative already-allocated value exactly once below.
  const matchedRule = evalData.matchedRule ?? evalData.rule;
  const storedMatchedRule = evalData.matchedRule ?? evalData.rule;
  const eligible = evalData.eligible ?? evalData.matched ?? !!matchedRule;

  // Persist matched rule to store so it's included in the grant API payload.
  // Skip when overrides are supplied — this isn't the wizard's in-progress grant.
  useEffect(() => {
    if (!storedMatchedRule || overrides) return;
    data.setMatchedRule(storedMatchedRule as any);
  }, [storedMatchedRule, overrides]);

  const isAmountMode = grantedByVal === GrantedBy.AMOUNT;
  const allocationMode =
    matchedRule?.allocationMode ?? AllocationMode.FIXED_OPTIONS;
  const isValueMode = allocationMode === AllocationMode.ABSOLUTE_VALUE;
  const costOrGain = matchedRule?.costOrGain ?? null;

  const employeeName =
    overrides?.employeeName || data.employee?.employeeName || "This recipient";

  function fmtQuantum(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value))
      return "—";
    if (isValueMode) return formatCurrency(value, currencyType);
    return `${value.toLocaleString(currencyType)} Options`;
  }

  const eligibilityChips = useMemo((): { label: string; value: string }[] => {
    if (evalData.matchedOn && Object.keys(evalData.matchedOn).length > 0) {
      return Object.entries(evalData.matchedOn).flatMap(([field, val]) => {
        const values = Array.isArray(val) ? val : [val];
        const label = formatFactorLabel(field);
        return values.map((v) => ({ label, value: v }));
      });
    }
    if (evalData.matchedConditions && evalData.matchedConditions.length > 0) {
      return evalData.matchedConditions.map((c) => ({
        label: c.label,
        value: c.value,
      }));
    }

    return [];
  }, [evalData.matchedOn, evalData.matchedConditions]);

  const isCatchAllRule = useMemo(() => {
    const conditions = matchedRule?.conditions;
    if (!conditions || Object.keys(conditions).length === 0) return true;

    return Object.values(conditions).every((values) =>
      values.some((value) => String(value).toLowerCase() === "any")
    );
  }, [matchedRule?.conditions]);

  const employeeGrants = useMemo(
    () =>
      (employeeGrantsData ?? []).filter(
        (g) =>
          g &&
          (g.grantIdentifier || g.id) &&
          g.optionHolderState !== "WITHDRAW" &&
          // Exclude the grant currently being edited so its options aren't
          // double-counted against the allocation limits.
          !(
            editingGrantId &&
            (g.grantIdentifier === editingGrantId || g.id === editingGrantId)
          )
      ),
    [employeeGrantsData, editingGrantId]
  );
  const grantsCount = employeeGrants.length;

  // For ABSOLUTE_VALUE rules: convert option count to monetary value using the current FMV.
  const calculatedAllocated = employeeGrants.reduce(
    (sum, g) =>
      sum +
      calculateAllocationValue({
        options: g.optionsGranted || 0,
        ratio: g.ratio,
        fmv: currentSharePrice,
        strikePrice: g.grantPrice || 0,
        allocationMode,
        costOrGain,
      }),
    0
  );
  const allocated =
    evalData.alreadyAllocated ??
    evalData.utilization?.allocated ??
    calculatedAllocated;

  // Grant By Amount + FIXED_OPTIONS: convert amount → options via GAIN/COST formula
  // GAIN: n = amount / (FMV - P_strike), COST: n = amount / FMV
  // ABSOLUTE_VALUE: display allocated as monetary value using GAIN/COST mode
  const currentSelection =
    isAmountMode && !isValueMode
      ? sharePrice > 0
        ? costOrGain === CostOrGain.GAIN
          ? Math.max(0, sharePrice - (grantPrice || 0)) > 0
            ? (amount || 0) / Math.max(0, sharePrice - (grantPrice || 0))
            : 0
          : (amount || 0) / sharePrice
        : 0
      : isValueMode
      ? costOrGain === CostOrGain.GAIN
        ? (optionsGranted || 0) *
          (data.plan?.conversionShares || 1) *
          Math.max(0, (sharePrice || 0) - (grantPrice || 0))
        : (optionsGranted || 0) *
          (data.plan?.conversionShares || 1) *
          (sharePrice || 0)
      : optionsGranted || 0;

  const isPerEmployee = matchedRule?.scope === RuleScope.PER_EMPLOYEE;
  const origFloor = matchedRule?.quantumMin ?? null;
  const origTarget = matchedRule?.quantumRecommended ?? null;
  const origCeiling = matchedRule?.quantumMax ?? null;
  const remaining = (v: number | null) =>
    v === null ? null : isPerEmployee ? Math.max(v - allocated, 0) : v;
  const floor = remaining(origFloor);
  const ceiling = remaining(origCeiling);
  const isAdjusted = isPerEmployee && allocated > 0;
  const isCapExhausted =
    isPerEmployee && ceiling !== null && ceiling <= 0 && allocated > 0;
  const displayEligible = eligible && !isCapExhausted;

  useEffect(() => {
    if (!evaluation || initializedTabForRef.current === initialTabKey) return;
    initializedTabForRef.current = initialTabKey;
    if (isCapExhausted) setActiveTab("utilization");
    else setActiveTab(eligible ? "rules" : "eligibility");
  }, [evaluation, initialTabKey, isCapExhausted, eligible]);

  // Fall back to midpoint when remaining recommended is ≤ 0.
  const remainingTarget = remaining(origTarget);
  const target =
    isAdjusted &&
    floor !== null &&
    ceiling !== null &&
    (remainingTarget === null || remainingTarget <= 0)
      ? Math.round((floor + ceiling) / 2)
      : remainingTarget;

  type EnvelopeStatus = "empty" | "below" | "within" | "above";
  let envelopeStatus: EnvelopeStatus = "within";
  if (!currentSelection) envelopeStatus = "empty";
  else if (floor !== null && currentSelection < floor) envelopeStatus = "below";
  else if (ceiling !== null && currentSelection > ceiling)
    envelopeStatus = "above";

  const statusBadge: Record<
    EnvelopeStatus,
    { label: string; className: string } | null
  > = {
    empty: null,
    below: { label: "Below Minimum", className: "bg-amber-100 text-amber-700" },
    within: { label: "Within Range", className: "bg-blue-100 text-blue-700" },
    above: { label: "Exceeds Maximum", className: "bg-rose-100 text-rose-600" },
  };

  const bounds = [
    floor !== null && {
      key: "floor",
      label: "Minimum",
      value: floor,
      original: origFloor as number,
      activeOn: "below" as EnvelopeStatus,
      activeClass: "border-amber-300 bg-amber-50",
    },
    target !== null && {
      key: "target",
      label: "Recommended",
      value: target,
      original: origTarget as number,
      activeOn: "within" as EnvelopeStatus,
      activeClass: "border-orange-300 bg-orange-50",
    },
    ceiling !== null && {
      key: "ceiling",
      label: "Maximum",
      value: ceiling,
      original: origCeiling as number,
      activeOn: "above" as EnvelopeStatus,
      activeClass: "border-rose-300 bg-rose-50",
    },
  ].filter(Boolean) as {
    key: string;
    label: string;
    value: number;
    original: number;
    activeOn: EnvelopeStatus;
    activeClass: string;
  }[];

  const util = evalData.utilization;
  const cap = util?.totalPool ?? origCeiling ?? null;
  const available =
    util?.available ?? (cap !== null ? Math.max(cap - allocated, 0) : null);
  const allocatedPct =
    cap && cap > 0 ? clamp((allocated / cap) * 100, 0, 100) : 0;

  const fmtCount = (v: number) =>
    isValueMode
      ? formatCurrency(v, currencyType)
      : v.toLocaleString(currencyType);

  if (!matrixId || !employeeId) return null;

  const visibleTabs = TABS.filter(
    (t) => t.id !== "utilization" || isPerEmployee
  );

  return (
    <VStack
      className={`${
        overrides?.embedded ? "" : "mx-6 mb-2 "
      }border border-gray-200 rounded-lg shadow-custom overflow-hidden`}
    >
      <HStack className="border-b border-gray-200">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 text-xs font-semibold uppercase tracking-wide border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-orange-501 text-orange-501"
                : "border-transparent text-gray-401 hover:text-black-501"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </HStack>

      <VStack className="p-5 gap-4">
        {isFetching && !evaluation && (
          <HStack className="items-center gap-2 text-sm text-gray-401">
            <Icon
              icon="ri:loader-4-line"
              className="animate-spin"
              height={16}
            />
            Evaluating allocation matrix…
          </HStack>
        )}

        {activeTab === "eligibility" && (
          <VStack className="gap-4">
            <HStack className="items-center gap-3">
              <div
                className={`flex items-center justify-center w-9 h-9 rounded-full ${
                  displayEligible
                    ? "bg-blue-100 text-blue-600"
                    : "bg-rose-100 text-rose-600"
                }`}
              >
                <Icon
                  icon={
                    displayEligible
                      ? "ri:checkbox-circle-fill"
                      : "ri:close-circle-fill"
                  }
                  height={22}
                />
              </div>
              <span className="text-base font-semibold text-black-501">
                {employeeName}{" "}
                {displayEligible ? "is eligible" : "is not eligible"}
              </span>
            </HStack>

            {displayEligible ? (
              <HStack className="flex-wrap gap-2">
                {eligibilityChips.length === 0 ? (
                  <span className="text-sm text-gray-401 italic">
                    {isCatchAllRule
                      ? "This rule applies to all recipients; no specific eligibility factors are configured."
                      : "No matching factors to display."}
                  </span>
                ) : (
                  eligibilityChips.map((chip) => (
                    <span
                      key={`${chip.label}:${chip.value}`}
                      className="px-3 py-1 rounded-md bg-gray-100 text-sm text-gray-600"
                    >
                      <span className="font-medium text-gray-500">
                        {chip.label}:
                      </span>{" "}
                      {chip.value}
                    </span>
                  ))
                )}
              </HStack>
            ) : (
              <p className="text-sm text-gray-401">
                {isCapExhausted
                  ? `Lifetime allocation cap fully utilised (${fmtQuantum(
                      allocated
                    )} of ${fmtQuantum(
                      origCeiling
                    )}). No headroom remains for a new grant.`
                  : evalData.message ??
                    (evalData.noMatchBehaviour
                      ? NO_MATCH_MESSAGE[evalData.noMatchBehaviour]
                      : "No allocation rule matched this recipient.")}
              </p>
            )}
          </VStack>
        )}

        {activeTab === "rules" && (
          <VStack className="gap-4">
            {!matchedRule ? (
              <p className="text-sm text-gray-401 italic">
                No matched rule to display an allocation envelope.
              </p>
            ) : (
              <>
                <HStack className="items-center justify-between">
                  <HStack className="items-center gap-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-401">
                      Allocation Envelope
                    </p>
                    {isValueMode && costOrGain && (
                      <FormulaTooltip costOrGain={costOrGain} />
                    )}
                  </HStack>
                  {statusBadge[envelopeStatus] && (
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        statusBadge[envelopeStatus]!.className
                      }`}
                    >
                      {statusBadge[envelopeStatus]!.label}
                    </span>
                  )}
                </HStack>

                {isAdjusted && (
                  <HStack className="items-start gap-2 text-xs text-gray-501">
                    <Icon
                      icon="ri:information-line"
                      className="text-gray-401 flex-shrink-0 mt-0.5"
                      height={14}
                    />
                    <span>
                      Remaining headroom after {fmtQuantum(allocated)} already
                      granted to this employee.{" "}
                      <button
                        type="button"
                        onClick={() => setActiveTab("utilization")}
                        className="font-semibold text-orange-501 hover:underline"
                      >
                        View utilization history
                      </button>
                    </span>
                  </HStack>
                )}

                <HStack className="gap-3">
                  {bounds.map((b) => {
                    const isActive = envelopeStatus === b.activeOn;
                    const showOriginal = isAdjusted && b.value !== b.original;
                    return (
                      <VStack
                        key={b.key}
                        className={`flex-1 gap-1 p-3 border rounded-lg transition-colors ${
                          isActive ? b.activeClass : "border-gray-200"
                        }`}
                      >
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-401">
                          {b.label}
                        </span>
                        <span className="text-lg font-semibold text-black-501">
                          {fmtQuantum(b.value)}
                        </span>
                        {showOriginal && (
                          <span className="text-xs text-gray-401">
                            of {fmtQuantum(b.original)}
                          </span>
                        )}
                      </VStack>
                    );
                  })}
                </HStack>

                {envelopeStatus === "empty" && !isCapExhausted ? (
                  <HStack className="items-center gap-2 p-3 rounded bg-gray-50 text-sm text-gray-501">
                    <Icon
                      icon="ri:information-line"
                      className="text-gray-401 flex-shrink-0"
                      height={18}
                    />
                    Enter the number of options below to see how it compares to
                    this rule.
                  </HStack>
                ) : envelopeStatus === "within" ? (
                  <HStack className="items-center gap-2 p-3 rounded bg-blue-50 text-sm text-blue-700">
                    <Icon
                      icon="ri:checkbox-circle-line"
                      className="flex-shrink-0"
                      height={18}
                    />
                    Your selection of {fmtQuantum(currentSelection)} is within
                    the recommended range for this rule.
                  </HStack>
                ) : envelopeStatus === "below" ? (
                  <HStack className="items-start gap-2 p-3 rounded bg-amber-50 text-sm text-amber-700">
                    <Icon
                      icon="ri:alert-line"
                      className="flex-shrink-0 mt-0.5"
                      height={18}
                    />
                    <span>
                      The selected amount ({fmtQuantum(currentSelection)}) falls
                      below the minimum of {fmtQuantum(floor)} defined by this
                      rule. This grant will be flagged for review during the
                      approval process.
                    </span>
                  </HStack>
                ) : (
                  <HStack className="items-start gap-2 p-3 rounded bg-rose-50 text-sm text-rose-600">
                    <Icon
                      icon="ri:error-warning-line"
                      className="flex-shrink-0 mt-0.5"
                      height={18}
                    />
                    <span>
                      Your selection of {fmtQuantum(currentSelection)} exceeds
                      the maximum of {fmtQuantum(ceiling)}. This grant will be
                      flagged during approval and review.
                    </span>
                  </HStack>
                )}
              </>
            )}
          </VStack>
        )}

        {activeTab === "utilization" && (
          <VStack className="gap-4">
            <HStack className="items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-401">
                Lifetime Cap Utilization
              </p>
              <button
                type="button"
                onClick={() =>
                  overrides?.onViewGrantHistory
                    ? overrides.onViewGrantHistory()
                    : navigate(`/options/employeeDetails/${employeeId}`)
                }
                className="flex items-center gap-1 text-xs font-semibold text-orange-501 hover:underline"
              >
                View Grant History
                <Icon icon="ri:arrow-right-up-line" height={14} />
              </button>
            </HStack>

            {grantsFetching && grantsCount === 0 ? (
              <HStack className="items-center gap-2 text-sm text-gray-401">
                <Icon
                  icon="ri:loader-4-line"
                  className="animate-spin"
                  height={16}
                />
                Loading grant history…
              </HStack>
            ) : (
              <>
                {cap !== null && (
                  <>
                    <HStack className="justify-between">
                      <VStack className="gap-0.5">
                        <span className="text-xs font-medium text-gray-401 uppercase">
                          Lifetime Cap
                        </span>
                        <span className="text-xl font-semibold text-black-501">
                          {fmtCount(cap)}
                        </span>
                      </VStack>
                      <VStack className="gap-0.5">
                        <HStack className="items-center gap-1">
                          <span className="text-xs font-medium text-gray-401 uppercase">
                            Allocated
                          </span>
                          <AllocatedTooltip
                            companyType={
                              companyData?.companyMetaData.typeOfTheCompany ??
                              ""
                            }
                            currentSharePrice={currentSharePrice}
                          />
                        </HStack>
                        <span className="text-xl font-semibold text-black-501">
                          {fmtCount(allocated)}
                        </span>
                      </VStack>
                      <VStack className="gap-0.5 text-right">
                        <span className="text-xs font-medium text-gray-401 uppercase">
                          Available
                        </span>
                        <span className="text-xl font-semibold text-green-600">
                          {fmtCount(available ?? 0)}
                        </span>
                      </VStack>
                    </HStack>

                    <div className="flex w-full h-2.5 rounded-full bg-blue-100 overflow-hidden">
                      <div
                        className="h-2.5 bg-orange-501"
                        style={{ width: `${allocatedPct}%` }}
                      />
                    </div>
                    <HStack className="gap-4 text-xs text-gray-401">
                      <HStack className="items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-orange-501" />
                        Allocated
                      </HStack>
                      <HStack className="items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-blue-100" />
                        Available
                      </HStack>
                    </HStack>
                  </>
                )}

                <HStack className="items-center gap-2 p-3 rounded bg-gray-50 text-sm text-gray-501">
                  <Icon
                    icon="ri:history-line"
                    className="text-gray-401 flex-shrink-0"
                    height={18}
                  />
                  {grantsCount === 0 ? (
                    "No previous grants issued to this recipient."
                  ) : (
                    <span>
                      This employee has {grantsCount} previous grant
                      {grantsCount === 1 ? "" : "s"}. Click View Grant History
                      to see them.
                    </span>
                  )}
                </HStack>
              </>
            )}
          </VStack>
        )}
      </VStack>
    </VStack>
  );
};

export default AllocationEnvelopePanel;
